// hooks
import { createContext, useContext } from 'react';

// enums
import { ToolbarPositionEnum } from './ToolbarWrapper.enums';

/**
 * Where the toolbar is docked, for the components a screen injects into its slots to read.
 *
 * They used to be handed the layout by `cloneElement(el, { isRightPositioned })`, which works for
 * exactly one element and silently does nothing for a fragment or a component that does not declare
 * the prop. The value is the `ToolbarPositionEnum` itself rather than a derived boolean, so there is
 * one source of truth and no illegal combination to get into.
 */
export const ToolbarPositionContext = createContext<ToolbarPositionEnum>(
  ToolbarPositionEnum.TOP
);

export const useToolbarPosition = () => useContext(ToolbarPositionContext);

/**
 * Whether the toolbar runs as a column rather than a bar — true for *both* docked positions, right
 * and zen.
 *
 * This is the widened meaning `cloneElement` passed down as `isRightPositioned || isZenPositioned`,
 * and keeping it widened is load-bearing: a consumer reading a narrow "is right" where the old code
 * handed it the wide one opens the Backlog poppers downwards in zen mode instead of to the left.
 * Derived on read so there is no second value to disagree with the enum.
 */
export const useIsVerticalToolbar = () =>
  useToolbarPosition() !== ToolbarPositionEnum.TOP;
